/**
 * Invitation Verification Page
 * Allows users to verify and accept workspace invitations
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Users,
  Building,
  Mail,
  Shield,
  ShieldCheck
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import { InvitationService } from '@/services/invitation-service'
import type { InvitationDetails } from '@/services/invitation-service'
import { authService } from '@/services/auth-service'
import { isAuthenticated, getUserData } from '@/utils/auth-storage'
import { withInvitationId } from '@/utils/invitation-url'

export function InvitationPage() {
  const { invitationId } = useParams()
  const navigate = useNavigate()

  const [invitationDetails, setInvitationDetails] = useState<InvitationDetails | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [isAccepting, setIsAccepting] = useState(false)
  // Set when the browser already has a session for a different email than
  // the invitation was sent to — surfaced instead of silently overwriting it.
  const [sessionMismatch, setSessionMismatch] = useState<{ sessionEmail: string } | null>(null)

  useEffect(() => {
    if (!invitationId) {
      setError('Invalid invitation link')
      setIsLoading(false)
      return
    }

    fetchInvitationDetails()
  }, [invitationId])

  const fetchInvitationDetails = async () => {
    try {
      setIsLoading(true)
      setError(null)

      const details = await InvitationService.getInvitationDetails(invitationId!)
      setInvitationDetails(details)
      setEmail(details.email) // Pre-fill the email field
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load invitation details'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleAcceptInvitation = async () => {
    if (!invitationId || !email.trim()) {
      toast.error('Please enter your email address')
      return
    }

    try {
      setIsAccepting(true)

      // If this browser already has a session, don't force a redundant
      // re-login (or worse, silently overwrite a different account's
      // session by routing through a signin form pre-filled with someone
      // else's email).
      if (isAuthenticated()) {
        const sessionUser = getUserData()
        const sessionEmail = sessionUser?.email?.trim().toLowerCase()
        const invitedEmail = email.trim().toLowerCase()

        if (sessionEmail && sessionEmail === invitedEmail) {
          // Already signed in as the invited user — skip signin/signup
          // entirely. InvitationHandler (wrapping /app) accepts the
          // invitation using the existing session and reloads once done.
          navigate(withInvitationId('/app', invitationId), { replace: true })
          return
        }

        if (sessionEmail && sessionEmail !== invitedEmail) {
          setSessionMismatch({ sessionEmail: sessionUser!.email })
          return
        }
      }

      // Check if the invitee already has an account so we know which flow to show.
      // Acceptance is DEFERRED until after the invitee proves identity (signs in
      // or signs up). Accepting here pre-auth would let anyone with the link
      // force-add the invitee to the workspace.
      const userCheck = await InvitationService.checkUserExists(email.trim())

      if (userCheck.exists) {
        navigate(`/signin?invitationId=${invitationId}`, {
          state: {
            message: 'Sign in to accept the workspace invitation.',
            email: email.trim(),
            fromInvitation: true,
            invitationId: invitationId,
            workspaceName: invitationDetails?.workspaceName,
          }
        })
      } else {
        navigate(`/signup?invitationId=${invitationId}`, {
          state: {
            fromInvitation: true,
            email: email.trim(),
            message: 'Create your account to join the workspace',
            invitationId: invitationId,
            workspaceName: invitationDetails?.workspaceName,
          }
        })
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to continue with invitation'
      toast.error(errorMessage)
    } finally {
      setIsAccepting(false)
    }
  }

  /**
   * Signs the current browser session out so the invited email can sign in
   * or sign up cleanly, then re-runs the accept flow for that email.
   */
  const handleSignOutAndContinue = async () => {
    setIsAccepting(true)
    try {
      // redirect: false — signout()'s default behavior is a hard navigation
      // to /signin, which would race with (and clobber) the navigation
      // handleAcceptInvitation is about to make below.
      await authService.signout({ redirect: false })
    } catch {
      // Best-effort — proceed regardless, local tokens are cleared either way.
    }
    setSessionMismatch(null)
    await handleAcceptInvitation()
  }

  const getRoleIcon = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
        return <ShieldCheck className="w-4 h-4" />
      case 'user':
      default:
        return <Shield className="w-4 h-4" />
    }
  }

  const getRoleBadgeVariant = (role: string): "default" | "secondary" | "outline" => {
    switch (role.toLowerCase()) {
      case 'admin':
        return 'default'
      case 'user':
      default:
        return 'outline'
    }
  }

  const getStatusIcon = (status: string | undefined, isExpired: boolean) => {
    if (isExpired) {
      return <XCircle className="w-5 h-5 text-destructive" />
    }

    if (!status) {
      return <Clock className="w-5 h-5 text-yellow-500" />
    }

    switch (status.toLowerCase()) {
      case 'accepted':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />
      case 'declined':
        return <XCircle className="w-5 h-5 text-destructive" />
      default:
        return <Clock className="w-5 h-5 text-yellow-500" />
    }
  }

  const formatExpiryDate = (expiredAt: string) => {
    const date = new Date(expiredAt)
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Skeleton className="h-8 w-64 mx-auto mb-2" />
            <Skeleton className="h-4 w-48 mx-auto" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  // Error state
  if (error || !invitationDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md mx-auto">
          <CardHeader className="text-center space-y-4">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            <div className="space-y-2">
              <CardTitle className="text-destructive">Invalid Invitation</CardTitle>
              <CardDescription className="break-words hyphens-auto text-sm leading-relaxed px-4">
                {error || 'This invitation link is invalid or has expired'}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate('/')}
              className="w-full"
              variant="outline"
            >
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const { isExpired, status } = invitationDetails || {}

  // Expired or already used invitation
  if (isExpired || status !== 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              {getStatusIcon(status, isExpired)}
            </div>
            <CardTitle className="text-muted-foreground">
              {isExpired ? 'Invitation Expired' : 'Invitation Already Used'}
            </CardTitle>
            <CardDescription>
              {isExpired
                ? 'This invitation has expired and can no longer be used'
                : 'This invitation has already been accepted'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Need a new invitation?</AlertTitle>
              <AlertDescription className="break-words hyphens-auto">
                Please contact {invitationDetails?.invitedByName || 'the admin'} to send you a new invitation.
              </AlertDescription>
            </Alert>
            <Button
              onClick={() => navigate('/')}
              className="w-full"
              variant="outline"
            >
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Signed in as a different account than the invitation was sent to —
  // don't silently overwrite that session by routing through signin/signup.
  if (sessionMismatch) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-yellow-600" />
            </div>
            <CardTitle>Different account signed in</CardTitle>
            <CardDescription className="break-words hyphens-auto">
              You're currently signed in as <strong>{sessionMismatch.sessionEmail}</strong>, but
              this invitation was sent to <strong>{email}</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>What would you like to do?</AlertTitle>
              <AlertDescription className="break-words hyphens-auto">
                Sign out of the current account to continue as {email}, or cancel and stay
                signed in as {sessionMismatch.sessionEmail}.
              </AlertDescription>
            </Alert>
            <div className="flex gap-3">
              <Button
                onClick={() => setSessionMismatch(null)}
                variant="outline"
                className="flex-1"
                disabled={isAccepting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSignOutAndContinue}
                className="flex-1 bg-primary hover:bg-primary/90"
                disabled={isAccepting}
              >
                {isAccepting ? 'Signing out...' : 'Sign out and continue'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Active invitation - show verification form
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg shadow-lg">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Workspace Invitation</CardTitle>
          <CardDescription>
            You've been invited to join a workspace
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Invitation Details */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Building className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium">{invitationDetails?.workspaceName || 'Unknown Workspace'}</p>
                <p className="text-sm text-muted-foreground">Workspace</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium">{invitationDetails?.invitedByName || 'Unknown'}</p>
                <p className="text-sm text-muted-foreground">Invited by</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-5 h-5 flex items-center justify-center">
                {getRoleIcon(invitationDetails?.role || 'user')}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Badge variant={getRoleBadgeVariant(invitationDetails?.role || 'user')} className="gap-1">
                    {getRoleIcon(invitationDetails?.role || 'user')}
                    {invitationDetails?.role || 'user'}
                  </Badge>
                  <span className="text-sm text-muted-foreground">Role</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2 border-t border-border/50">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Expires on</p>
                <p className="text-xs text-muted-foreground">
                  {formatExpiryDate(invitationDetails?.expiredAt || new Date().toISOString())}
                </p>
              </div>
            </div>
          </div>

          {/* Email Verification Form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Verify your email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="focus:ring-primary focus:border-primary"
                disabled={isAccepting}
              />
              <p className="text-xs text-muted-foreground">
                This must match the email address the invitation was sent to
              </p>
            </div>

            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertTitle>What happens next?</AlertTitle>
              <AlertDescription className="break-words hyphens-auto">
                After accepting this invitation, you'll be automatically redirected to sign in or create an account to join the workspace.
              </AlertDescription>
            </Alert>

            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="flex-1"
                disabled={isAccepting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAcceptInvitation}
                className="flex-1 bg-primary hover:bg-primary/90"
                disabled={isAccepting || !email.trim()}
              >
                {isAccepting ? 'Accepting...' : 'Accept Invitation'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}